import { createContext, useContext, useEffect, useState, ReactNode, FC } from 'react';
import { User } from 'firebase/auth';
import { firebaseAuthApi } from '../api/firebaseAuthApi';
import { authApi, UserProfile } from '../api/authApi';

interface AuthContextType {
    firebaseUser: User | null;
    userProfile: UserProfile | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    loginWithCustomToken: (customToken: string) => Promise<void>;
    logout: () => Promise<void>;
    refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
    const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        console.log('🔐 AuthProvider: 인증 상태 감지 시작');

        const unsubscribe = firebaseAuthApi.onAuthStateChanged(async (user) => {
            console.log('🔐 Firebase 사용자:', user?.email || '없음');
            setFirebaseUser(user);

            if (user) {
                try {
                    const token = await user.getIdToken();
                    console.log('🔑 Firebase 토큰 발급 완료');

                    const response = await authApi.verifyFirebaseToken(token);
                    console.log('✅ 백엔드 인증 완료:', response.result);

                    setUserProfile(response.result);
                    localStorage.setItem('user', JSON.stringify(response.result));
                } catch (error) {
                    console.error('❌ 백엔드 인증 실패:', error);
                    setUserProfile(null);
                    localStorage.removeItem('user');
                }
            } else {
                console.log('👤 로그인되지 않음');
                setUserProfile(null);
                localStorage.removeItem('user');
            }

            setLoading(false);
            console.log('✅ 인증 상태 확인 완료');
        });

        return () => unsubscribe();
    }, []);

    const login = async (email: string, password: string) => {
        console.log('🔐 로그인 시도:', email);
        setLoading(true);
        try {
            const userCredential = await firebaseAuthApi.loginWithEmailPassword({
                email,
                password,
            });

            const token = await userCredential.user.getIdToken();
            const response = await authApi.verifyFirebaseToken(token);

            setUserProfile(response.result);
            localStorage.setItem('user', JSON.stringify(response.result));
            console.log('✅ 로그인 성공');
        } catch (error) {
            console.error('❌ 로그인 실패:', error);
            throw new Error((error as Error).message || '로그인에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const loginWithCustomToken = async (customToken: string) => {
        console.log('🔐 커스텀 토큰 로그인 시도');
        setLoading(true);
        try {
            const userCredential = await firebaseAuthApi.loginWithCustomToken({
                customToken,
            });

            const token = await userCredential.user.getIdToken();
            const response = await authApi.verifyFirebaseToken(token);

            setUserProfile(response.result);
            localStorage.setItem('user', JSON.stringify(response.result));
            console.log('✅ 커스텀 토큰 로그인 성공');
        } catch (error) {
            console.error('❌ 커스텀 토큰 로그인 실패:', error);
            throw new Error((error as Error).message || '커스텀 토큰 로그인에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        console.log('🔐 로그아웃');
        await firebaseAuthApi.logout();
        setFirebaseUser(null);
        setUserProfile(null);
        localStorage.removeItem('user');
    };

    const refreshToken = async () => {
        if (firebaseUser) {
            const token = await firebaseUser.getIdToken(true);
            const response = await authApi.verifyFirebaseToken(token);
            setUserProfile(response.result);
            localStorage.setItem('user', JSON.stringify(response.result));
        }
    };

    const value: AuthContextType = {
        firebaseUser,
        userProfile,
        loading,
        login,
        loginWithCustomToken,
        logout,
        refreshToken,
    };

    console.log('🔐 AuthContext 상태:', {
        loading,
        hasUser: !!firebaseUser,
        hasProfile: !!userProfile
    });

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
